import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/AuthContext';
import { usePermission } from '@/core/permissions';
import { inventoryListService } from '../services/inventoryList.service';
import type { InventoryFilters, InventoryStatus } from '../types/inventoryList.types';

const defaultFilters: InventoryFilters = {
  search: '',
  status: 'all',
  category: '',
  warehouse: '',
  itemType: '',
  supplier: '',
  stockStatus: '',
  page: 1,
  pageSize: 10,
};

export function useInventoryList() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const { can, isAdmin } = usePermission();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';
  const [filters, setFilters] = useState<InventoryFilters>(defaultFilters);
  const stableFilters = useMemo(() => filters, [filters]);

  const query = useQuery({
    queryKey: ['inventory', 'list', companyId, userId, stableFilters],
    queryFn: () => inventoryListService.getInventoryList(companyId, stableFilters, userId),
    enabled: !!companyId && !!userId,
  });

  const groupsQuery = useQuery({
    queryKey: ['inventory', 'groups', companyId],
    queryFn: () => inventoryListService.getGroups(companyId),
    enabled: !!companyId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => inventoryListService.deleteInventoryItem(id, companyId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'list'] });
      toast.success(' xa vt t thnh cng');
    },
    onError: (error: Error) => toast.error(`Li khi xa vt t: ${error.message}`),
  });

  const updateSearch = (search: string) => {
    setFilters((current) => ({ ...current, search, page: 1 }));
  };

  const updateStatus = (status: 'all' | InventoryStatus) => {
    setFilters((current) => ({ ...current, status, page: 1 }));
  };

  const updateFilter = (key: keyof InventoryFilters, value: any) => {
    setFilters((current) => ({ ...current, [key]: value, page: 1 }));
  };

  const updatePage = (page: number) => {
    setFilters((current) => ({ ...current, page }));
  };

  const updatePageSize = (pageSize: number) => {
    setFilters((current) => ({ ...current, pageSize, page: 1 }));
  };

  const filterOptions = useMemo(() => {
    const rawData = groupsQuery.data || [];
    const childrenMap = new Map<string, any[]>();
    const roots: any[] = [];

    rawData.forEach((item: any) => {
      if (!item.parent_id) {
        roots.push(item);
      } else {
        if (!childrenMap.has(item.parent_id)) {
          childrenMap.set(item.parent_id, []);
        }
        childrenMap.get(item.parent_id)!.push(item);
      }
    });

    const flattened: { label: string, value: string }[] = [];

    const traverse = (items: any[], prefix: string, level: number) => {
      items.forEach((item, index) => {
        const seq = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
        const indent = level > 1 ? '\u00A0\u00A0\u00A0\u00A0'.repeat(level - 1) : '';
        flattened.push({
          label: `${indent}${seq}. ${item.name}`,
          value: item.id
        });
        
        const children = childrenMap.get(item.id);
        if (children && children.length > 0) {
          traverse(children, seq, level + 1);
        }
      });
    };

    traverse(roots, '', 1);

    const baseOptions = query.data?.filterOptions || { categories: [], warehouses: [], suppliers: [] };
    
    return {
      ...baseOptions,
      categories: flattened.length > 0 ? flattened : baseOptions.categories
    };
  }, [groupsQuery.data, query.data?.filterOptions]);

  return {
    data: query.data ?? {
      items: [],
      total: 0,
      stats: {
        totalItems: 0,
        totalValue: 0,
        lowStockCount: 0,
        outOfStockCount: 0,
        activeCount: 0,
        inactiveCount: 0,
      },
      filterOptions: { categories: [], warehouses: [], suppliers: [] },
    },
    filters,
    filterOptions,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isDeleting: deleteMutation.isPending,
    error: query.error,
    canDelete: isAdmin || can('inventory', 'delete'),
    deleteItem: deleteMutation.mutateAsync,
    updateSearch,
    updateStatus,
    updateFilter,
    updatePage,
    updatePageSize,
  };
}
